import * as React from "react";
import { useRef } from "react";
import TextField, { Props } from "../TextField";
import { TextFieldProps } from "@mui/material/TextField";
import AutoNumeric from "autonumeric";
import InputAdornment from "../InputAdornment";

type CurrencyTextFieldProps = {
  type?: "text" | "tel" | "hidden";
  /** The variant to use. */
  variant?: string;
  id?: string;
  /** The CSS class name of the wrapper element. */
  className?: string;
  /** Inline styling for element */
  style?: object;
  /** If true, the input element will be disabled. */
  disabled?: boolean;
  /** The label content. */
  label?: string;
  /** Align the numbers in the textField.
   * If you pass the `inputProps` from TextFieldAPI text align won't work.
   * then, you have handle it by className with your own class inside inputProps.
   */
  textAlign?: "right" | "left" | "center";
  /** Tab index for the element */
  tabIndex?: number;
  /** If true, the input element will be focused during the first mount. */
  autoFocus?: boolean;
  /** The short hint displayed in the input before the user enters a value. */
  placeholder?: string;
  /** value to be enter and display in input */
  value?: string | number;
  /** Callback fired when the value is changed. */
  onChange?: () => {} | undefined;
  /** Callback fired when focused on element. */
  onFocus?: () => {} | undefined;
  /** Callback fired on blur. */
  onBlur?: () => {} | undefined;
  /** Callback fired on key press. */
  onKeyPress?: () => {} | undefined;
  /** Callback fired on key press. */
  onKeyUp?: () => {} | undefined;
  /** Callback fired on key press. */
  onKeyDown?: () => {} | undefined;
  /** Defines the currency symbol string. */
  currencySymbol?: string;
  /** Defines what decimal separator character is used. */
  decimalCharacter?: string;
  /** Allow to declare an alternative decimal separator which is automatically replaced by `decimalCharacter` when typed. */
  decimalCharacterAlternative?: string;
  /** Defines the default number of decimal places to show on the formatted value. */
  decimalPlaces?: number;
  /** Defines how many decimal places should be visible when the element is unfocused null. */
  decimalPlacesShownOnBlur?: number;
  /** Defines how many decimal places should be visible when the element has the focus. */
  decimalPlacesShownOnFocus?: number;
  /** Defines the thousand grouping separator character */
  digitGroupSeparator?: string;
  /** Controls the leading zero behavior   */
  leadingZero?: "allow" | "deny" | "keep";
  /** maximum value that can be enter */
  maximumValue?: string;
  /** minimum value that can be enter */
  minimumValue?: string;
  /** placement of the negitive and possitive sign symbols */
  negativePositiveSignPlacement?: "l" | "r" | "p" | "s";
  /** Defines the negative sign symbol to use   */
  negativeSignCharacter?: string;
  /** how the value should be formatted,before storing it */
  outputFormat?: "string" | "number";
  /** Defines if the element value should be selected on focus. */
  selectOnFocus?: boolean;
  /** Defines the positive sign symbol to use. */
  positiveSignCharacter?: string;
  /** Defines if the element should be set as read only on initialization. */
  readOnly?: boolean;
  /** predefined objects are available in <a href="https://www.nodenpm.com/autonumeric/4.5.1/detail.html#predefined-options">AutoNumeric</a>*/
  preDefined?: object;
} & TextFieldProps;

export const CurrencyTextField = React.forwardRef(
  (props: CurrencyTextFieldProps, ref: any) => {
    const { currencySymbol, inputProps, InputProps, ...others } = props;

    const otherProps = {};
    [
      "id",
      "label",
      "className",
      "autoFocus",
      "variant",
      "style",
      "error",
      "disabled",
      "type",
      "name",
      "defaultValue",
      "tabIndex",
      "fullWidth",
      "rows",
      "rowsmax",
      "select",
      "required",
      "helperText",
      "unselectable",
      "margin",
      "SelectProps",
      "multiline",
      "size",
      "FormHelperTextProps",
      "placeholder",
    ].forEach((prop) => (otherProps[prop] = props[prop]));

    var autonumeric: AutoNumeric;
    const refInputTextField = useRef(null);

    React.useEffect(() => {
      // Update the document title using the browser API

      const { currencySymbol, ...others } = props;
      autonumeric?.remove();
      autonumeric = new AutoNumeric(refInputTextField.current, props.value, {
        ...props.preDefined,
        ...others,
        outputFormat: "number",
        // onChange: undefined,
        // onFocus: undefined,
        // onBlur: undefined,
        // onKeyPress: undefined,
        // onKeyUp: undefined,
        // onKeyDown: undefined,
        // watchExternalChanges: false,
      });
      const isValueChanged = getValue() !== props.value;
      if (isValueChanged) {
        autonumeric.set(props.value);
      }
    }, [props.value]);

    const getValue = () => {
      if (!autonumeric) return;
      const valueMapper = {
        string: (numeric) => numeric.getNumericString(),
        number: (numeric) => numeric.getNumber(),
      };
      return valueMapper[props.outputFormat](autonumeric);
    };

    const callEventHandler = (event, eventName) => {
      if (!props[eventName]) return;
      props[eventName](event, getValue());
    };

    return (
      <TextField
        inputRef={refInputTextField}
        onChange={(e) => callEventHandler(e, "onChange")}
        onFocus={(e) => callEventHandler(e, "onFocus")}
        onBlur={(e) => callEventHandler(e, "onBlur")}
        onKeyPress={(e) => callEventHandler(e, "onKeyPress")}
        onKeyUp={(e) => callEventHandler(e, "onKeyUp")}
        onKeyDown={(e) => callEventHandler(e, "onKeyDown")}
        InputProps={{
          startAdornment: currencySymbol && (
            <InputAdornment position="start">{currencySymbol}</InputAdornment>
          ),
          ...InputProps,
        }}
        sx={{
          "input ": {
            textAlign: props.textAlign || "right",
            paddingLeft: 0,
            paddingRight: 0,
            cursor: "auto",
          },
          "& .MuiInputBase-root.MuiOutlinedInput-root": {
            backgroundColor:
              props.readOnly && "primary.inputBackgroundReadonlyColor",
          },
        }}
        {...otherProps}
        ref={ref}
      />
    );
  }
);
CurrencyTextField.defaultProps = {
  type: "text",
  variant: "outlined",
  // currencySymbol: "$",
  outputFormat: "number",
  textAlign: "right",
  maximumValue: "10000000000000",
  minimumValue: "-10000000000000",
};

export const predefinedOptions = AutoNumeric.getPredefinedOptions();
